/* eslint-disable no-restricted-syntax */
import LoadingMessage from './Loading';
import { TailLeftIcon } from './TailLeftIcon';
import { TailRightIcon } from './TailRightIcon';
import { MarkdownRenderer } from '#components';
import { Avatar, Box, Stack, Paper, Theme } from '@mui/material';
import { ForwardRefRenderFunction, forwardRef } from 'react';

type Props = {
  variant: 'right' | 'left';
  content: string | undefined;
  loading?: boolean;
  avatarUrl?: string;
  fullWidth?: boolean;
  onLinkClick?: (url: string, target: '_blank' | '_self' | '_parent' | '_top' | null) => void;
};
/**
 * Message Component
 *
 * This component renders a chat bubble for the AI assistant interface.
 * It supports two variants: 'right' for user messages and 'left' for AI responses.
 *
 * @component
 * @param {Object} props - The component props
 * @param {'right' | 'left'} props.variant - Determines the style and positioning of the chat bubble
 * @param {string | undefined} props.content - The text content of the chat bubble
 * @param {boolean} [props.loading] - Indicates if the message is in a loading state
 *
 * @example
 * <Message variant="left" content="Hello! How can I assist you today?" />
 * <Message variant="left" loading={true} />
 */

const Message: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { content, variant, loading, avatarUrl, fullWidth, onLinkClick },
  ref
) => {
  return (
    <Stack
      alignItems="end"
      direction="row"
      spacing={1}
      justifyContent={variant === 'right' ? 'flex-end' : 'flex-start'}
    >
      {avatarUrl && (
        <Avatar
          sx={{
            width: 24,
            height: 24,
            // A transparent background prevents the theme from adding the primary color behind the image.
            backgroundColor: 'transparent',
            borderRadius: '50%',
          }}
          src={avatarUrl}
        />
      )}
      <Paper
        ref={ref}
        sx={{
          position: 'relative',
          maxWidth: loading ? '20%' : fullWidth ? '100%' : '70%',
          alignSelf: variant === 'right' ? 'flex-end' : undefined,
          padding: 1.5,
          background: (theme: Theme) =>
            variant === 'right' ? theme.palette.primary.main : theme.palette.secondary.dark,
          color: (theme: Theme) => (variant === 'right' ? theme.palette.primary.contrastText : undefined),
        }}
      >
        {content && <MarkdownRenderer markdown={content} onClick={onLinkClick} />}
        {loading && <LoadingMessage />}

        {variant === 'right' && (
          // This is the "tail" of the bubble on the right side
          <Box
            sx={{
              position: 'absolute',
              bottom: '-6px',
              right: '-9px',
              color: (theme: Theme) => theme.palette.primary.main,
            }}
          >
            <TailRightIcon />
          </Box>
        )}

        {variant === 'left' && (
          // This is the "tail" of the bubble on the left side
          <Box
            sx={{
              position: 'absolute',
              bottom: '-6px',
              left: '-9px',
              color: (theme: Theme) => theme.palette.secondary.dark,
            }}
          >
            <TailLeftIcon />
          </Box>
        )}
      </Paper>
    </Stack>
  );
};

export default forwardRef(Message);
